import { FC, ReactNode } from "react";
import { useSelector } from "react-redux";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { Button } from "@remo-co/ui-core/src/components/Button";
import { Container } from "@remo-co/ui-core/src/components/Container";
import { Avatar } from "modules/avatar";
import { CompanyLogo } from "modules/company/components/CompanyLogo";
import { selectMyCompany } from "modules/company/redux/selectors";
import ImagePlaceHolder from "modules/uploader/ImagePlaceHolder";
import { getProfileAvatar } from "helpers/profileHelper";
import {
  selectIsAnonymousUser,
  selectUser,
} from "modules/auth/redux/selectors";
import { useI18n } from "i18n";
import { Calendar as IconCalendar } from "@remo-co/ui-core/src/icons/Calendar";
import { Web as IconWeb } from "@remo-co/ui-core/src/icons/Web";
import { useStyles } from "./styles";
import { ReactComponent as IconSansan } from "./icons/assets/Sansan.inline.svg";
import { ReactComponent as IconLinkedIn } from "./icons/assets/LinkedIn.inline.svg";
import { ReactComponent as IconFacebook } from "./icons/assets/Facebook.inline.svg";
import ProfileLink from "./icons/ProfileLink";
import { getUserName } from "./utils";

const Cell: FC<{ label: string; value: string | ReactNode }> = ({
  label,
  value,
}) => {
  const classes = useStyles();

  return (
    <div className={classes.itemCell}>
      <Typography variant="subtitle2">{label}</Typography>
      <div className={classes.itemValue}>
        {typeof value === "string" ? (
          <Typography data-testid={`${label}-value`}>{value}</Typography>
        ) : (
          value
        )}
      </div>
    </div>
  );
};

interface IProfileInfo {
  onEdit(): void;
}

const ProfileInfo = ({ onEdit }: IProfileInfo) => {
  const user = useSelector(selectUser);
  const { t } = useI18n(["common", "profile", "menu"]);
  const company = useSelector(selectMyCompany);
  const classes = useStyles();

  const isUserAnonymous = useSelector(selectIsAnonymousUser);

  const showEditProfile = !isUserAnonymous;

  const headline =
    user && user.profile.headline ? user.profile.headline : "N/A";
  const companyName =
    user && user.profile.company ? user.profile.company : "N/A";
  const profile = user && user.profile.title ? user.profile.title : "N/A";

  return (
    <Container className={classes.container}>
      <Container className={classes.header}>
        <Avatar
          pictureUrl={getProfileAvatar(user)}
          className={classes.avatar}
        />
        <Container className={classes.edit} fullWidth={false}>
          <Typography variant="h1">{user ? getUserName(user) : ""}</Typography>
          <Typography variant="subtitle1">
            {isUserAnonymous ? user?.id : user?.email || ""}
          </Typography>
          {showEditProfile && (
            <Container top="xs" fullWidth={false}>
              <Button variant="primary" color="blue" size="md" onClick={onEdit}>
                <Typography variant="body1">
                  {t("profile:edit.profile")}
                </Typography>
              </Button>
            </Container>
          )}
        </Container>
      </Container>
      <Cell label={t("profile:headline")} value={headline} />
      <Container flex justifyContent="flex-start">
        <Cell label={t("profile:company")} value={companyName} />
        <Cell label={t("profile:job.title")} value={profile} />
      </Container>
      <Cell
        label={t("profile:personal.links")}
        value={
          <Container flex justifyContent="flex-start" className={classes.icons}>
            <ProfileLink
              href={user?.profile?.meetingLink}
              label={t("eventForm:book.meeting")}
            >
              <IconCalendar className={classes.linkIcons} />
            </ProfileLink>
            <ProfileLink
              href={user?.profile?.facebookProfileURL}
              label={t("eventForm:facebook.profile")}
            >
              <IconFacebook />
            </ProfileLink>
            <ProfileLink
              href={user?.profile?.linkedInProfileURL}
              label={t("eventForm:linkedin.profile")}
            >
              <IconLinkedIn />
            </ProfileLink>
            <ProfileLink
              href={user?.profile?.sansanProfileURL}
              label={t("eventForm:sansan.profile")}
            >
              <IconSansan />
            </ProfileLink>
            <ProfileLink href={user?.profile?.website} label={t("website")}>
              <IconWeb className={classes.linkIcons} />
            </ProfileLink>
          </Container>
        }
      />
      {company && (
        <div className={classes.companyContainer}>
          <Typography color="textPrimary" variant="h5">
            {t("profile:company.profile")}
          </Typography>
          <Container flex justifyContent="flex-start" top={15}>
            <div className={classes.companyImage}>
              {company.logoURL ? (
                <CompanyLogo url={company.logoURL} />
              ) : (
                <ImagePlaceHolder />
              )}
            </div>
            <Typography color="textPrimary" variant="h3">
              {company.name !== "" ? company.name : t("menu:unnamed.company")}
            </Typography>
          </Container>
        </div>
      )}
    </Container>
  );
};

export default ProfileInfo;
